import { Avatar, Box, ModalSlideshow } from '@dev-spendesk/grapes';
import { useState } from 'react';
import { Trans } from 'react-i18next';

import { useTranslation } from 'common/hooks/useTranslation';
import { useUserRoles } from 'modules/app/hooks/useUserRoles';
import { useIsUkKycRejected } from 'src/core/common/hooks/useIsUkKycRejected';
import FEATURES from 'src/core/constants/features';
import { LocalStorageKey } from 'src/core/constants/storage';
import { useIsUkInternationalWireTransferEnabled } from 'src/core/modules/invoices/transfer';
import { AnalyticEventName, track } from 'src/core/utils/analytics';
import { useFeature } from 'src/onboarding/common/hooks/useFeature';

import { useCompany } from '../../../../modules/app/hooks/useCompany';

const trackNotInterested = () => {
  track(AnalyticEventName.DASHBOARD_IWT_TEASER_CLOSED, {
    source: 'Modal',
  });
};

const trackViewed = () => {
  track(AnalyticEventName.DASHBOARD_IWT_TEASER_VIEWED, {
    source: 'Modal',
  });
};

const trackAcknowledged = () => {
  track(AnalyticEventName.DASHBOARD_IWT_TEASER_CLICKED, {
    source: 'Modal',
  });
};

export const InternationalWireTransferFeaturePreAdoptionModal = () => {
  const { t } = useTranslation('global');

  const userRoles = useUserRoles();

  const isTargetRole =
    userRoles.isAccountOwner || userRoles.isAdmin || userRoles.isController;

  const hasBeenClosed = localStorage.getItem(
    LocalStorageKey.IwtAdoptionModalClosedDate,
  );

  const hasEEAInternationalWiretransferFeature = useFeature(
    FEATURES.TMP_EEA_INTERNATIONAL_WIRE_TRANSFER,
  );

  const company = useCompany();

  const isEea = company.currency === 'EUR';

  const isUKCompanyAllowed = useIsUkInternationalWireTransferEnabled();

  const isEEACompanyAllowed = hasEEAInternationalWiretransferFeature && isEea;

  const isHomepage = window.location.pathname.includes('/home');

  const isUkKycRejected = useIsUkKycRejected(!hasBeenClosed);

  const [isOpen, setIsOpen] = useState(
    isHomepage &&
      isTargetRole &&
      !hasBeenClosed &&
      !isUkKycRejected &&
      (isUKCompanyAllowed || isEEACompanyAllowed),
  );

  const closeModal = () => {
    setIsOpen(false);
    localStorage.setItem(
      LocalStorageKey.IwtAdoptionModalClosedDate,
      new Date().toISOString(),
    );

    if (company.currency === 'GBP') {
      window.location.href = `/app/${company.id}/company/integrations/all`;
    }
  };

  if (!isOpen) {
    return null;
  }

  trackViewed();

  const renderSlideContent = () => {
    return (
      <>
        <p className="text-secondary-bg-primary">
          {isEea ? (
            t('international-wiretransfer.adoptionModal.eea.description')
          ) : (
            <ul className="list-inside list-disc [&_*]:pl-12">
              <Trans i18nKey="international-wiretransfer.adoptionModal.uk.description">
                <li>-</li>
                <li>-</li>
                <li>-</li>
                <li>-</li>
              </Trans>
            </ul>
          )}
        </p>
        {isEea && (
          <Box
            variant="secondary"
            className="text-primary mt-16 flex items-center gap-16"
          >
            <Avatar
              src="/static/img/avatars/Avatar_Men_Arabic_Glasses-Young.png"
              text="Happy Customer"
              size={40}
              variant="circle"
              className="flex-shrink-0"
            />
            <p>
              {t(
                'international-wiretransfer.adoptionModal.eea.customerSatisfaction',
              )}
            </p>
          </Box>
        )}
      </>
    );
  };

  return (
    <ModalSlideshow
      slides={[
        {
          title: isEea
            ? t('international-wiretransfer.adoptionModal.eea.title')
            : t('international-wiretransfer.adoptionModal.uk.title'),
          content: renderSlideContent(),
          illustration: (
            <img
              src="/static/img/internationalTransfers/globe.svg"
              alt="international wire transfer illustration"
            />
          ),
        },
      ]}
      translations={{
        cancel: isEea
          ? t('international-wiretransfer.adoptionModal.eea.notInterested')
          : t('international-wiretransfer.adoptionModal.uk.notInterested'),
        previous: t('misc.back'),
        next: t('misc.next'),
        done: isEea
          ? t('international-wiretransfer.adoptionModal.eea.soundsGreat')
          : t('international-wiretransfer.adoptionModal.uk.soundsGreat'),
      }}
      isOpen={isOpen}
      onCancel={() => {
        trackNotInterested();
        closeModal();
      }}
      onClose={() => {
        trackNotInterested();
        closeModal();
      }}
      onDone={() => {
        trackAcknowledged();
        closeModal();
      }}
    />
  );
};
